import { useQuery } from '@tanstack/react-query'
import { FARA_NUMAR, verificaLivrare, type CampLivrare, type DateLivrare } from '@b4l/shared'
import { api } from '../../../lib/api'

/**
 * Datele de livrare ale unei cereri trimise prin curier.
 *
 * Madalin, 16.10.2026: „să necesite detalii exacte … tot ce este important pentru un AWB CORECT".
 * De aceea județul și localitatea NU se scriu de mână: se aleg din lista FAN Courier. O localitate
 * scrisă altfel decat o are FAN e un AWB refuzat, sau dus în altă parte.
 *
 * Regulile câmpurilor vin din `@b4l/shared` — aceleași pe care serverul le aplică la trimitere.
 * Aici doar se arată din timp; ciorna se poate salva și incompletă.
 */
export type Livrare = Required<{ [K in CampLivrare]: string }>

export const LIVRARE_GOALA: Livrare = {
  nume: '',
  contact: '',
  telefon: '',
  email: '',
  judet: '',
  localitate: '',
  strada: '',
  numar: '',
  bloc: '',
  scara: '',
  etaj: '',
  apartament: '',
  codPostal: '',
}

export function livrareDin(l: DateLivrare | null | undefined): Livrare {
  const o = { ...LIVRARE_GOALA }
  for (const k of Object.keys(o) as CampLivrare[]) o[k] = l?.[k] ?? ''
  return o
}

export function BlocLivrare({
  value,
  onChange,
  cuFirma,
  arataErori,
}: {
  value: Livrare
  onChange: (l: Livrare) => void
  /** La partener, pe AWB scrie firma, iar contactul e separat. Altfel, numele e chiar contactul. */
  cuFirma: boolean
  /** Erorile se arată doar după ce omul a încercat să bifeze sau să trimită, nu de la prima literă. */
  arataErori: boolean
}) {
  const pune = (k: CampLivrare, v: string) => onChange({ ...value, [k]: v })
  const erori = arataErori
    ? verificaLivrare(cuFirma ? value : { ...value, nume: value.contact })
    : {}

  const judete = useQuery({
    queryKey: ['fan-judete'],
    staleTime: 3600_000,
    queryFn: async () =>
      (await api.get<{ judete: string[] }>('/marketing/necesar/livrare/judete')).data.judete,
  })
  const localitati = useQuery({
    queryKey: ['fan-localitati', value.judet],
    enabled: !!value.judet,
    staleTime: 3600_000,
    queryFn: async () =>
      (
        await api.get<{ localitati: { nume: string; kmExtra: number | null }[] }>(
          '/marketing/necesar/livrare/localitati',
          { params: { judet: value.judet } },
        )
      ).data.localitati,
  })
  const faraNumar = value.numar === FARA_NUMAR
  const kmExtra = localitati.data?.find((l) => l.nume === value.localitate)?.kmExtra

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {cuFirma && (
          <Camp
            eticheta="Firma (pe AWB)"
            value={value.nume}
            onChange={(v) => pune('nume', v)}
            eroare={erori.nume}
          />
        )}
        <Camp
          eticheta="Persoana de contact"
          value={value.contact}
          onChange={(v) => pune('contact', v)}
          eroare={erori.contact}
          autoComplete="name"
        />
        <Camp
          eticheta="Telefon"
          value={value.telefon}
          onChange={(v) => pune('telefon', v)}
          eroare={erori.telefon}
          inputMode="tel"
          autoComplete="tel"
        />
        <Camp
          eticheta="Email"
          value={value.email}
          onChange={(v) => pune('email', v)}
          eroare={erori.email}
          inputMode="email"
          autoComplete="email"
        />
        <Alege
          eticheta="Județ"
          value={value.judet}
          optiuni={judete.data ?? []}
          incarca={judete.isLoading}
          // La schimbarea județului, localitatea veche nu mai e validă.
          onChange={(v) => onChange({ ...value, judet: v, localitate: '' })}
          eroare={erori.judet}
        />
        <Alege
          eticheta="Localitate"
          value={value.localitate}
          optiuni={(localitati.data ?? []).map((l) => l.nume)}
          incarca={localitati.isLoading}
          dezactivat={!value.judet}
          gol={value.judet ? undefined : 'alege întâi județul'}
          onChange={(v) => pune('localitate', v)}
          eroare={erori.localitate}
        />
        <Camp
          eticheta="Strada"
          value={value.strada}
          onChange={(v) => pune('strada', v)}
          eroare={erori.strada}
        />
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Camp
              eticheta="Număr"
              value={faraNumar ? '' : value.numar}
              onChange={(v) => pune('numar', v)}
              eroare={faraNumar ? undefined : erori.numar}
              dezactivat={faraNumar}
            />
            <label className="mt-1 flex items-center gap-1 text-[11px] text-b4l-text-muted">
              <input
                type="checkbox"
                checked={faraNumar}
                onChange={(e) => pune('numar', e.target.checked ? FARA_NUMAR : '')}
              />
              fără număr
            </label>
          </div>
          <Camp
            eticheta="Cod poștal"
            value={value.codPostal}
            onChange={(v) => pune('codPostal', v)}
            eroare={erori.codPostal}
            inputMode="numeric"
          />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-2">
        <Camp eticheta="Bloc" value={value.bloc} onChange={(v) => pune('bloc', v)} />
        <Camp eticheta="Scara" value={value.scara} onChange={(v) => pune('scara', v)} />
        <Camp eticheta="Etaj" value={value.etaj} onChange={(v) => pune('etaj', v)} />
        <Camp eticheta="Ap." value={value.apartament} onChange={(v) => pune('apartament', v)} />
      </div>
      {judete.isError && (
        <p className="text-xs text-b4l-red-dark">
          Lista de localități FAN nu se poate citi acum. Poți salva ciorna și reveni; cererea prin
          curier se trimite doar cu adresa aleasă din listă.
        </p>
      )}
      {kmExtra != null && kmExtra > 0 && (
        <p className="text-xs text-b4l-amber-dark">
          {value.localitate} e la {kmExtra} km în afara ariei FAN — transportul costă în plus.
        </p>
      )}
    </div>
  )
}

function Camp({
  eticheta,
  value,
  onChange,
  eroare,
  dezactivat,
  inputMode,
  autoComplete,
}: {
  eticheta: string
  value: string
  onChange: (v: string) => void
  eroare?: string
  dezactivat?: boolean
  inputMode?: 'tel' | 'email' | 'numeric'
  autoComplete?: string
}) {
  return (
    <label className="flex flex-col gap-0.5 text-[11px] text-b4l-text-muted">
      {eticheta}
      <input
        value={value}
        disabled={dezactivat}
        inputMode={inputMode}
        autoComplete={autoComplete}
        aria-invalid={!!eroare}
        onChange={(e) => onChange(e.target.value)}
        className={`rounded border px-2 py-1 text-sm disabled:bg-b4l-bg ${
          eroare ? 'border-b4l-red' : 'border-b4l-border'
        }`}
      />
      {eroare && <span className="text-[11px] text-b4l-red-dark">{eroare}</span>}
    </label>
  )
}

function Alege({
  eticheta,
  value,
  optiuni,
  onChange,
  eroare,
  incarca,
  dezactivat,
  gol,
}: {
  eticheta: string
  value: string
  optiuni: string[]
  onChange: (v: string) => void
  eroare?: string
  incarca?: boolean
  dezactivat?: boolean
  gol?: string
}) {
  /* O valoare veche, scrisă de mână înainte de liste, rămâne vizibilă ca să se vadă ce trebuie ales. */
  const lipsesteDinLista = value && !optiuni.includes(value) && optiuni.length > 0
  return (
    <label className="flex flex-col gap-0.5 text-[11px] text-b4l-text-muted">
      {eticheta}
      <select
        value={value}
        disabled={dezactivat || incarca}
        aria-invalid={!!eroare || !!lipsesteDinLista}
        onChange={(e) => onChange(e.target.value)}
        className={`rounded border px-2 py-1 text-sm disabled:bg-b4l-bg ${
          eroare || lipsesteDinLista ? 'border-b4l-red' : 'border-b4l-border'
        }`}
      >
        <option value="">{incarca ? 'Se încarcă…' : (gol ?? '— alege —')}</option>
        {lipsesteDinLista && <option value={value}>{value} (nu e în lista FAN)</option>}
        {optiuni.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
      {eroare && <span className="text-[11px] text-b4l-red-dark">{eroare}</span>}
      {!eroare && lipsesteDinLista && (
        <span className="text-[11px] text-b4l-red-dark">Alege din listă varianta corectă.</span>
      )}
    </label>
  )
}
